/******************************************************************************
 *** BgtSpcWholinq ** 특별회계기금 * 수입/지출결의서 전체 조회
*******************************************************************************/
import React from "react";
import Util from "@/js/common/Util";
import SelectBox from "@/js/common/ui/input/SelectBox";
import StyledTabs from "@/js/common/ui/StyledTabs";
import { Tab, TabList, TabPanel } from "react-tabs";
import { useRecoilValue } from "recoil";
import * as Atom from "@/js/common/Atom";
import BgtSpcWholDpstList from "./BgtSpcWholDpstList";
import BgtSpcWholExpndList from "./BgtSpcWholExpndList";
import TextBox from "@/js/common/ui/input/TextBox";
import DatePickerBox from "@/js/common/ui/input/DatePickerBox";
import AppContext from "@/js/common/AppContext";

interface Props {
    systemCode: string;
}
const BgtSpcWholinq = ( {systemCode}: Props ) => {
    const userInfo = useRecoilValue(Atom.userInfo);
    // 검색 조건
    const [upOgnzCd        , setUpOgnzCd        ] = React.useState<string>(userInfo.upOgnzCd);      // 상위조직코드(본부)
    const [ognzCd          , setOgnzCd          ] = React.useState<string>(userInfo.ognzCd  );      // 조직코드(지부)
    const [acntgYear       , setAcntgYear       ] = React.useState<string>(Util.getNowAcntgYear()); // 회계연도
    const [searchAtrzSttsCd, setSearchAtrzSttsCd] = React.useState<string>(""); // 진행상태
    const [searchKywd      , setSearchKywd      ] = React.useState<string>(""); // 검색어(적요)
    const [acntgMonth      , setAcntgMonth      ] = React.useState<string>(""); // 월별 조회
    
    // 수입일자/행위년월일 
    const [sttDt, setSttDt] = React.useState<string>(""); // 시작일자
    const [endDt, setEndDt] = React.useState<string>(""); // 종료일자

    // 지출결의서 검색 조건
    const [expndRsdocSeCd  , setExpndRsdocSeCd  ] = React.useState(""); // 결의서 구분
    const [dataSbmsnCmptnYn, setDataSbmsnCmptnYn] = React.useState(""); // 자료제출 여부
    // Tabs
    const [tabIndex, setTabIndex] = React.useState<number>(0); // 0: 수입, 1: 지출

    // 특별회계기금 타이틀 설정
    const [systemName, setSystemName] = React.useState<string>("");

    // 특별회계기금 타이틀 설정
    React.useEffect(() => {
        if (systemCode === "C1") {
            setSystemName("복지기금");
        } else if (systemCode === "C2") {
            setSystemName("자립기금");
        } else if (systemCode === "C3") {
            setSystemName("발전기금");
        } else if (systemCode === "C4") {
            setSystemName("희생자부조금");
        } else if (systemCode === "C5") {
            setSystemName("퇴직급여충당금");
        }
    }, [systemCode]);

    return (
        <div className="w1200 bgcWhite p20 bdrd8 mt10 boxShadowSoft">
            {/* 타이틀 */}
            <div className="mt5">
                <span className="dpBlock w100p fm-c fs25 fc2c3e50 ls1 pb10">{systemName} 전체 조회</span>
                <div className="bdt3 bdc007bff pb10" />
            </div>

            {/* 검색 영역 */}
            <div className="bd1 bdcDDDDDD p20 mb10 bdrd6 bgcFAFAFA">
                <div className="dpFlx aiC flxWrap gap14">
                    {/* 소속 */}
                    <div className="dpFlx aiC">
                        <span className="w50 fm-c fs15 fc333333 ls1 taL">본부</span>
                        <SelectBox value={upOgnzCd}
                            width={150} height={36} fontSize={14} fontFamily="fm-c" color="#333" letterSpacing={-1}
                            borderColor="#ddd" borderOnColor="#222" borderRadius={2}
                            background="#F6F6F6" paddingRight={8}
                            codeArray={Util.getUpOrgCodeArray("01")}
                            placeholder="전체"
                            onChangeFunc={(data: string) => setUpOgnzCd(data)}
                            disabled={true}
                        />
                    </div>

                    {/* 지부 */}
                    <div className="dpFlx aiC">
                        <span className="w50 fm-c fs15 fc333333 ls1 taL">지부</span>
                        <SelectBox value={ognzCd}
                            width={180} height={36}
                            fontSize={14} fontFamily="fm-c" color="#333" letterSpacing={-1}
                            borderColor="#ddd" borderOnColor="#222" borderRadius={2}
                            background="#F6F6F6" paddingRight={8}
                            codeArray={Util.getDownOrgCodeArray(upOgnzCd)}
                            placeholder="전체"
                            onChangeFunc={(data: string) => setOgnzCd(data)}
                            disabled={true}
                        />
                    </div>

                    {/* 회계연도 */}
                    <div className="dpFlx aiC">
                        <span className="w80 fm-c fs15 fc333333 ls1 taL">회계연도</span>
                        <SelectBox value={acntgYear}
                            width={120} height={36}
                            fontSize={14} fontFamily="fm-c" color="#333" letterSpacing={-1}
                            borderColor="#ddd" borderOnColor="#222" borderRadius={2}
                            background="#fff" paddingRight={8}
                            codeArray={Util.getAcntgYearArray()}
                            placeholder="선택"
                            onChangeFunc={(data: string) => setAcntgYear(data)}
                        />
                    </div>

                    {/* 월별 조회 */}
                    <div className="dpFlx aiC">
                        <span className="w80 fm-c fs15 fc333333 ls1 taL">월별 조회</span>
                        <SelectBox value={acntgMonth}
                            width={140} height={36}
                            fontSize={14} fontFamily="fm-c" color="#333" letterSpacing={-1}
                            borderColor="#ddd" borderOnColor="#222" borderRadius={2}
                            background="#fff" paddingRight={8}
                            codeArray={[{ code: "", name: "전체" }].concat(Util.getCodeArray("SRCH_MM"))}
                            placeholder="전체"
                            onChangeFunc={(data: string) => setAcntgMonth(data)}
                        />
                    </div>

                    {/* 진행상태 */}
                    <div className="dpFlx aiC">
                        <span className="w80 fm-c fs15 fc333333 ls1 taL">진행상태</span>
                        <SelectBox value={searchAtrzSttsCd}
                            width={140} height={36}
                            fontSize={14} fontFamily="fm-c" color="#333" letterSpacing={-1}
                            borderColor="#ddd" borderOnColor="#222" borderRadius={2}
                            background="#fff" paddingRight={8}
                            codeArray={[{ code: "", name: "전체" }].concat(Util.getCodeArray("ATRZ_STTS_CD"))}
                            placeholder="전체"
                            onChangeFunc={(data: string) => setSearchAtrzSttsCd(data)}
                        />
                    </div>

                    {/* 적요 */}
                    <div className="dpFlx aiC">
                        <span className="w50 fm-c fs15 fc333333 ls1 taL">적요</span>
                        <TextBox value={searchKywd}
                            width={200} height={36}
                            fontSize={14} fontFamily="fm-c" color="#333" letterSpacing={-1}
                            borderColor="#ddd" borderOnColor="#222" borderRadius={2}
                            style={{ backgroundColor: "#fff", padding: "0 12px" }}
                            placeholder="검색어 입력"
                            onChangeFunc={(text: string) => setSearchKywd(text)}
                        />
                    </div>

                    {/* 작성일자 기간 */}
                    <div className="dpFlx aiC">
                        <span className="w90 fm-c fs15 fc333333 ls1 taL">{tabIndex === 0 ? "수입일자" : "행위년월일"}</span>
                        <DatePickerBox value={sttDt}
                            width={150} height={36}
                            fontSize={14} fontFamily="fm-c" color="#333" letterSpacing={-1}
                            borderColor="#ddd" borderOnColor="#222" borderRadius={2}
                            bgColor="white" textAlign="left"
                            calendarThemeColor={AppContext.themeColor}
                            className="mr5"
                            onChange={(data: string) => setSttDt(data)}
                        />
                        <span className="fm-c fc666666 mr5">~</span>
                        <DatePickerBox value={endDt}
                            width={150} height={36}
                            fontSize={14} fontFamily="fm-c" color="#333" letterSpacing={-1}
                            borderColor="#ddd" borderOnColor="#222" borderRadius={2}
                            bgColor="white" textAlign="left"
                            calendarThemeColor={AppContext.themeColor}
                            className="mr5"
                            onChange={(data: string) => setEndDt(data)}
                        />
                    </div>

                    {/* 지출결의서 구분 */}
                    {tabIndex === 1 && (
                        <div className="dpFlx aiC">
                            <span className="w90 fm-c fs15 fc333333 ls1 taL">결의서 구분</span>
                            <SelectBox value={expndRsdocSeCd}
                                width={140} height={36}
                                fontSize={14} fontFamily="fm-c" color="#333" letterSpacing={-1}
                                borderColor="#ddd" borderOnColor="#222" borderRadius={2}
                                background="#fff" paddingRight={8}
                                codeArray={[{ code: "00", name: "전체" }].concat(Util.getCodeArray("EXPND_RSDOC_SE_CD"))}
                                placeholder="전체"
                                onChangeFunc={(v: string) => {
                                    setExpndRsdocSeCd(v);
                                    if (v !== "02") setDataSbmsnCmptnYn("");
                                }}
                            />
                        </div>
                    )}

                    {/* 자료제출 여부 */}
                    {tabIndex === 1 && expndRsdocSeCd === "02" && (
                        <div className="dpFlx aiC">
                            <span className="w100 fm-c fs15 fc333333 ls1 taL">자료제출 여부</span>
                            <SelectBox value={dataSbmsnCmptnYn}
                                width={120} height={36}
                                fontSize={14} fontFamily="fm-c" color="#333" letterSpacing={-1}
                                borderColor="#ddd" borderOnColor="#222" borderRadius={2}
                                background="#fff" paddingRight={8}
                                codeArray={[{ code: "", name: "전체" }].concat(Util.getCodeArray("UI_SBMSN_YN_01"))}
                                placeholder="전체"
                                onChangeFunc={(v: string) => setDataSbmsnCmptnYn(v)}
                            />
                        </div>
                    )}
                </div>
            </div>

            {/* 탭 */}
            <StyledTabs selectedIndex={tabIndex} className="mb10 tabFit">
                <TabList>
                    <Tab onClick={() => setTabIndex(0)}>수입 결의서</Tab>
                    <Tab onClick={() => setTabIndex(1)}>지출 결의서</Tab>
                </TabList>

                {/* ========================= 수입 탭 ========================= */}
                <TabPanel>
                    <BgtSpcWholDpstList
                        ognzCd           = {ognzCd}       // 조직
                        acntgYear        = {acntgYear}    // 회계연도
                        systemCode       = {systemCode}   // 시스테코드
                        searchAtrzSttsCd = {searchAtrzSttsCd}   // 진행상태
                        searchKywd       = {searchKywd} // 검색어(적요)
                        sttDt            = {sttDt           } // 검색 시작일(수입일자 기준)
                        endDt            = {endDt           } // 검색 종료일(수입일자 기준)
                        setEndDt         = {setEndDt        } // set 검색 종료일(초기화 프롭스)
                        acntgMonth       = {acntgMonth      } // 월별 조회
                    ></BgtSpcWholDpstList>
                </TabPanel>

                {/* ========================= 지출 탭 ========================= */}
                <TabPanel>
                    <BgtSpcWholExpndList
                        ognzCd           = {ognzCd}       // 조직
                        acntgYear        = {acntgYear}    // 회계연도
                        systemCode       = {systemCode}   // 시스템코드
                        searchAtrzSttsCd = {searchAtrzSttsCd}   // 진행상태
                        searchKywd       = {searchKywd} // 검색어(적요)
                        expndRsdocSeCd   = {expndRsdocSeCd}  // 결의서 구분
                        dataSbmsnCmptnYn = {dataSbmsnCmptnYn} // 자료제출 여부
                        sttDt            = {sttDt           } // 검색 시작일(행위년월일 기준)
                        endDt            = {endDt           } // 검색 종료일(행위년월일 기준)
                        setEndDt         = {setEndDt        } // set 검색 종료일(초기화 프롭스)
                        acntgMonth       = {acntgMonth      } // 월별 조회
                    ></BgtSpcWholExpndList>
                </TabPanel>
            </StyledTabs>
    </div>
  );
};

export default BgtSpcWholinq;
